import { useContext, useState } from 'react';
import { CalendarFilter as ApiCalendarFilter, CompanyLocationsListPreviewOutDTO } from '@api/ringil-api.v2';
import { useFilter } from '@hooks/useFilter/Filter.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { isNotNullOrUndefined } from '@utils/utils';

/**
 * The filter shape is owned by the API (`CalendarFilter`), because saved filters are persisted with
 * it — the field names here must stay exactly the ones the backend stores and returns.
 *
 * `locationIds`/`rampIds` drive the reservation request; everything else is highlight-only and is
 * applied to the loaded events (see calendarHighlight.ts).
 */
export type CalendarFilter = ApiCalendarFilter;

export const EMPTY_CALENDAR_FILTER: CalendarFilter = {
  locationIds: [],
  rampIds: [],
  applicationIds: [],
  carPlates: [],
  carrierIds: [],
  expeditionApplicationIds: [],
  supplierIds: [],
};

/**
 * A location is ours when none of the "someone else's" flags is set; anything external, shared with
 * us, inherited from a customer or belonging to a partner counts as foreign. The calendar filter
 * offers the two sets as separate trees over the same request keys.
 */
export const isOwnLocation = (location: Pick<CompanyLocationsListPreviewOutDTO, 'isExternal' | 'isPartnerLocation' | 'isSharedWithMe' | 'isInheritedFromCustomer'>): boolean =>
  !location.isExternal && !location.isPartnerLocation && !location.isSharedWithMe && !location.isInheritedFromCustomer;

/** `[parentKey, childKey]` of the locations → ramps tree filter. */
export const CALENDAR_FILTER_FIELDS = ['locationIds', 'rampIds'];

/** Fields that only grey out / outline the loaded events; the fetch ignores them entirely. */
export const CALENDAR_HIGHLIGHT_FIELDS = ['applicationIds', 'carPlates', 'carrierIds', 'expeditionApplicationIds', 'supplierIds'];

export const CALENDAR_FILTER_NAME = 'timeslot-calendar';

// Where the calendar kept its selection before it moved onto the shared filter (per company, no URL).
const LEGACY_LOCATIONS_KEY = 'TIMESLOT_SELECTED_LOCATIONIDS';
const LEGACY_RAMPS_KEY = 'TIMESLOT_SELECTED_RAMPS_OBJECT';

const readLegacyKey = (key: string, companyId: string): string[] => {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    const value = parsed?.[companyId];
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

/**
 * Location/ramp filter of the timeslot calendar, on the same `useFilter` plumbing as every list page:
 * the selection lives in the query string (so a day + set of ramps is a shareable link) and in
 * per-user local storage.
 *
 * The pre-migration selection (per-company local storage, no URL) is read once as the default filter,
 * so nobody loses their ramps the first time they open the calendar after this ships. Nothing writes
 * those keys any more — the seed can be dropped once every user has been through here.
 */
export const useCalendarFilter = () => {
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const [defaultFilter] = useState<CalendarFilter>(() => ({
    ...EMPTY_CALENDAR_FILTER,
    locationIds: readLegacyKey(LEGACY_LOCATIONS_KEY, loggedUser.companyId),
    rampIds: readLegacyKey(LEGACY_RAMPS_KEY, loggedUser.companyId),
  }));

  const { filter, applyFilterValue, applyFilter } = useFilter<CalendarFilter>({
    // The calendar reloads off the filter values themselves (see useCalendarData), so there is
    // nothing to do here — this hook only owns the URL/storage round-trip.
    onLoad: () => {},
    filterName: CALENDAR_FILTER_NAME,
    defaultFilter,
    arrayFields: [...CALENDAR_FILTER_FIELDS, ...CALENDAR_HIGHLIGHT_FIELDS],
    // The calendar is not paged — keep pageNumber/pageSize out of its URL.
    pagingInUrl: false,
  });

  return {
    filter,
    applyFilterValue,
    applyFilter,
    /** `useFilter` resolves its initial value in an effect — until then there is nothing to fetch. */
    ready: isNotNullOrUndefined(filter),
  };
};